import React from 'react'
import { Layout, Typography, Button, Row, Col, Card } from 'antd'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { siteSettingsApi, serviceContentApi } from '../../services/api'
import { EnvironmentOutlined, PhoneOutlined, GlobalOutlined } from '@ant-design/icons'

const { Content } = Layout

const ServicePage: React.FC = () => {
  const { i18n, t } = useTranslation()
  const currentLang = i18n.language as 'en' | 'zh'

  // Fetch site settings for dynamic content
  const { data: siteSettingsData } = useQuery({
    queryKey: ['publicSiteSettings'],
    queryFn: siteSettingsApi.getPublicSettings,
    staleTime: 1000 * 60 * 5, // 5 minutes
  })

  // Fetch service content
  const { data: serviceContentData } = useQuery({
    queryKey: ['publicServiceContent'],
    queryFn: serviceContentApi.getPublicContent,
    staleTime: 1000 * 60 * 5, // 5 minutes
  })

  const settings = siteSettingsData?.settings
  const serviceContent = serviceContentData?.content

  // Helper function to get localized text
  const getLocalizedText = (text: { en: string; zh: string } | undefined, fallback: string = '') => {
    if (!text) return fallback
    return text[currentLang] || text.en || fallback
  }



  return (
    <>
      <Helmet>
        <title>{t('service.title')} - {getLocalizedText(settings?.siteName, 'MedCareHub')}</title>
        <meta name="description" content={t('service.subtitle')} />
      </Helmet>

      <Content>
        {/* Hero Section: video or image (locale-aware) */}
        <section className="relative flex items-center justify-center h-[60vh] text-white">
          {(() => {
            const banner = settings?.pages?.service?.banner?.[currentLang];
            if (banner?.mediaType === 'video' && banner?.videoUrl) {
              return (
                <video className="absolute inset-0 w-full h-full object-cover" src={banner.videoUrl} autoPlay muted loop playsInline />
              );
            }
            const bgUrl = banner?.imageUrl || settings?.pages?.service?.bannerImage || '/servicepagebanner.webp';
            return (
              <div className="absolute inset-0 bg-center bg-cover" style={{ backgroundImage: `url('${bgUrl}')`, backgroundColor: '#1890ff' }} />
            );
          })()}
          <div className="absolute inset-0 bg-black bg-opacity-40"></div>
          <div className="max-w-6xl mx-auto px-4 text-center relative z-10">
            <Typography.Title level={1} className="text-4xl font-bold text-white mb-4 drop-shadow-lg" style={{ color: '#ffffff' }}>
              {getLocalizedText(settings?.pages?.service?.title) || t('service.title')}
            </Typography.Title>
            <Typography.Paragraph className="text-xl text-white max-w-3xl mx-auto drop-shadow-lg">
              {getLocalizedText(settings?.pages?.service?.subtitle) || t('service.subtitle')}
            </Typography.Paragraph>
          </div>
        </section>


        {/* Contacts Section (dynamic) */}
        {serviceContent?.contacts && serviceContent.contacts.length > 0 && (
          <section className="py-16 bg-white">
            <div className="max-w-6xl mx-auto px-4">
              <div className="text-center mb-12">
                <Typography.Title level={2} className="text-3xl font-bold text-gray-900 mb-4">
                  {getLocalizedText(serviceContent?.contactSection?.title, 'Contact Information')}
                </Typography.Title>
                <Typography.Paragraph className="text-lg text-gray-600">
                  {getLocalizedText(serviceContent?.contactSection?.subtitle, 'Get in touch with our authorized distributors')}
                </Typography.Paragraph>
              </div>
              <Row gutter={[24, 24]} justify="center">
                {serviceContent.contacts.map((contact: any) => (
                  <Col xs={24} lg={12} key={contact.id}>
                    <Card className="h-full border-2 border-blue-400">
                      <Typography.Title level={4} className="text-lg font-semibold mb-4 text-red-600">
                        {contact.type}
                      </Typography.Title>
                      
                      <div className="mb-4">
                        <Typography.Title level={5} className="text-base font-medium mb-2 text-red-600">
                          {getLocalizedText(contact.companyName)}
                        </Typography.Title>
                        {getLocalizedText(contact.description) && (
                          <Typography.Paragraph className="text-sm text-gray-600 mb-2">
                            {getLocalizedText(contact.description)}
                          </Typography.Paragraph>
                        )}
                        <Typography.Paragraph className="text-sm text-red-600 mb-1">
                          {getLocalizedText(contact.address)}
                        </Typography.Paragraph>
                        <Typography.Paragraph className="text-sm text-red-600 mb-2">
                          <PhoneOutlined /> Tel: {contact.phone}
                        </Typography.Paragraph>
                        {contact.website && (
                          <Typography.Paragraph className="text-sm text-red-600 mb-4">
                            <GlobalOutlined /> Website: {contact.website}
                          </Typography.Paragraph>
                        )}
                      </div>
                      
                      <Button 
                        type="primary" 
                        danger 
                        size="large"
                        icon={<EnvironmentOutlined />}
                        href={contact.googleMapsLink || `https://maps.google.com/maps?q=${encodeURIComponent(getLocalizedText(contact.address))}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        View on Google Maps
                      </Button>
                    </Card>
                  </Col>
                ))}
              </Row>
            </div>
          </section>
        )}






      </Content>
    </>
  )
}

export default ServicePage